/**
 * rolly-cart.js — shared add-to-cart-and-open-drawer helper.
 *
 * Exposes window.rolly_addToCartAndOpen(variantId, quantity, triggerEl, options).
 * options.beforeOpen: optional promise (e.g. the add-to-bag animation) to wait for
 * before the drawer opens — capped, and a rejection never blocks the drawer.
 * Loaded globally in theme.liquid before any page-specific scripts.
 * Vanilla JS only — no framework dependencies.
 *
 * This helper posts to /cart/add.js directly and so never runs Dawn's
 * product-form.js or cart.js. Those are where theme 15.5.0 emits the standard
 * cart events, which meant every add on this storefront — PDP and collection
 * alike — mutated the cart silently: apps, pixels and shopping agents saw the
 * cart change with no idea what was added. The dispatch below restores that
 * signal, mirroring product-form.js's contract exactly (same action, context,
 * line shape and deferred promise) so consumers cannot tell the two apart.
 */

/**
 * Announce an imminent cart mutation. Returns a deferred whose promise the
 * event carries; consumers may await it to learn the resulting cart. Returns
 * null when the StandardEvents module is unavailable, so every call site stays
 * optional — the cart must keep working if the module fails to load.
 */
function rolly_createCartLinesUpdateEvent(target, variantId, quantity) {
  var CartLinesUpdateEvent = (window.StandardEvents || {}).CartLinesUpdateEvent;
  if (!CartLinesUpdateEvent) return null;

  var deferred = CartLinesUpdateEvent.createPromise();
  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: quantity }],
      promise: deferred.promise,
    })
  );
  return deferred;
}

/**
 * Settle the deferred with the post-mutation cart. /cart/add.js returns only
 * the added line, not the cart aggregates, so re-read the full cart — the same
 * reason Dawn's own resolveCartLinesUpdate does.
 */
function rolly_resolveCartLinesUpdate(deferred) {
  if (!deferred) return;
  var CartLinesUpdateEvent = (window.StandardEvents || {}).CartLinesUpdateEvent;
  if (!CartLinesUpdateEvent) return;

  var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
  var pending = typeof CartItems !== 'undefined' && CartItems.fetchCartData
    ? CartItems.fetchCartData()
    : fetch(cartUrl + '.json').then(function (r) { return r.json(); });

  pending
    .then(function (cart) {
      if (!cart || !cart.currency) {
        return deferred.reject(new Error('Missing currency in cart response'));
      }
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
    })
    .catch(function (e) { deferred.reject(e); });
}

function rolly_dispatchCartErrorEvent(target, message, code) {
  var CartErrorEvent = (window.StandardEvents || {}).CartErrorEvent;
  if (!CartErrorEvent) return;
  target.dispatchEvent(new CartErrorEvent({ error: message, code: code }));
}

var ROLLY_BEFORE_OPEN_MAX_MS = 1500; // never hold the drawer longer than this

/** Resolve when options.beforeOpen settles (or after the cap); never rejects. */
function rolly_waitBeforeOpen(options) {
  var pending = options && options.beforeOpen;
  if (!pending || typeof pending.then !== 'function') return Promise.resolve();
  return Promise.race([
    pending.catch(function (err) { console.warn('[rolly-cart] beforeOpen failed; opening the drawer anyway.', err); }),
    new Promise(function (resolve) { setTimeout(resolve, ROLLY_BEFORE_OPEN_MAX_MS); }),
  ]);
}

window.rolly_addToCartAndOpen = function (variantId, quantity, triggerEl, options) {
  var cartDrawer = document.querySelector('cart-drawer');
  var useDrawer  = cartDrawer &&
                   typeof cartDrawer.renderContents === 'function' &&
                   typeof cartDrawer.getSectionsToRender === 'function';
  var body;
  var headers = {};

  if (useDrawer) {
    var fd = new FormData();
    fd.append('id', variantId);
    fd.append('quantity', String(quantity || 1));
    var ids = cartDrawer.getSectionsToRender().map(function (s) { return s.id; }).join(',');
    fd.append('sections', ids);
    fd.append('sections_url', window.location.pathname);
    headers['X-Requested-With'] = 'XMLHttpRequest';
    body = fd;
  } else {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify({ id: variantId, quantity: quantity || 1 });
  }

  if (triggerEl) triggerEl.disabled = true;

  // Dispatch from the trigger so the event bubbles through the real DOM path;
  // fall back to document for programmatic calls with no originating element.
  var eventTarget = (triggerEl && triggerEl.isConnected) ? triggerEl : document;
  var qty = quantity || 1;
  var linesUpdate = rolly_createCartLinesUpdateEvent(eventTarget, variantId, qty);

  // One add produces exactly one outcome. Rejecting an already-settled deferred
  // is harmless, but dispatching a second CartErrorEvent is not — without this
  // flag an API error fired both INVALID (below) and SERVICE_UNAVAILABLE (from
  // the catch, via the rethrow), and a cart mutation that had already succeeded
  // could still be reported as failed if rendering the drawer threw.
  var settled = false;

  return fetch('/cart/add.js', { method: 'POST', headers: headers, body: body })
    .then(function (r) { return r.json(); })
    .then(function (response) {
      if (response && response.status) {
        var message = response.description || response.message || 'Add to cart failed';
        settled = true;
        rolly_dispatchCartErrorEvent(eventTarget, message, 'INVALID');
        if (linesUpdate) linesUpdate.reject(new Error(message));
        throw new Error(message);
      }
      // Settle before touching the UI: the event describes the cart mutation,
      // which has already happened. A rendering failure must not retroactively
      // report the add itself as failed.
      settled = true;
      rolly_resolveCartLinesUpdate(linesUpdate);
      return rolly_waitBeforeOpen(options).then(function () {
        if (useDrawer) {
          if (typeof cartDrawer.setActiveElement === 'function') cartDrawer.setActiveElement(triggerEl);
          cartDrawer.classList.remove('is-empty');
          cartDrawer.renderContents(response);
        } else {
          window.location.href = '/cart';
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: response }));
        return response;
      });
    })
    .catch(function (err) {
      if (!settled) {
        settled = true;
        rolly_dispatchCartErrorEvent(eventTarget, String((err && err.message) || err), 'SERVICE_UNAVAILABLE');
        if (linesUpdate) linesUpdate.reject(err);
      }
      if (triggerEl) triggerEl.disabled = false;
      throw err;
    })
    .finally(function () {
      if (triggerEl) triggerEl.disabled = false;
    });
};
